/* Bol Customers — bol_orders'tan agregated müşteri listesi + detay drawer. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Users, Search, RefreshCw, Loader2, Mail, MapPin, Building2,
    Package, Euro, TrendingUp, X, ExternalLink, CheckCircle2, Truck, XCircle, Briefcase,
    FileSpreadsheet,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtEur = (n) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);
const statusBadge = (s) => {
    if (s === 'open') return { cls: 'bg-amber-100 text-amber-800', txt: 'Açık' };
    if (s === 'shipped') return { cls: 'bg-emerald-100 text-emerald-700', txt: 'Gönderildi' };
    if (s === 'cancelled') return { cls: 'bg-gray-100 text-gray-500', txt: 'İptal' };
    return { cls: 'bg-blue-50 text-blue-700', txt: s || '—' };
};

export default function BolCustomers() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('last_order');
    const [filter, setFilter] = useState('all'); // all | b2b | b2c
    const [exporting, setExporting] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/bol/customers`, {
                headers: hdrs(),
                params: { search, sort, limit: 500 },
            });
            setItems(r.data?.items || []);
            setSummary(r.data?.summary || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Müşteriler yüklenemedi');
        } finally { setLoading(false); }
    }, [search, sort]);

    useEffect(() => { load(); }, [load]);

    const exportXlsx = async () => {
        setExporting(true);
        try {
            const r = await axios.get(`${API}/api/bol/customers/export.xlsx`, {
                headers: hdrs(),
                params: { search, sort, filter },
                responseType: 'blob',
            });
            const disposition = r.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `bol-musteriler-${Date.now()}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Excel indirildi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Excel indirilemedi');
        } finally { setExporting(false); }
    };

    const filteredItems = items.filter((c) => {
        if (filter === 'b2b') return c.is_b2b;
        if (filter === 'b2c') return !c.is_b2b;
        return true;
    });

    return (
        <div className="space-y-4" data-testid="bol-customers">
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Card label="Toplam Müşteri" value={summary.total_customers} icon={Users} color="blue" />
                    <Card label="B2B Müşteri" value={`${summary.b2b_customers || 0} (${summary.b2b_rate || 0}%)`} icon={Briefcase} color="indigo" />
                    <Card label="Tekrar Eden" value={`${summary.repeat_customers} (${summary.repeat_rate}%)`} icon={TrendingUp} color="emerald" />
                    <Card label="Toplam Ciro" value={fmtEur(summary.total_revenue)} icon={Euro} color="purple" />
                    <Card label="Ort. Sipariş" value={fmtEur(summary.avg_order_value)} icon={Package} color="amber" />
                </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Email, isim, şirket, şehir veya BTW ara..."
                        className="w-full h-9 pl-8 pr-3 text-sm border border-gray-300 rounded-md focus:border-blue-500 outline-none"
                        data-testid="bc-search" />
                </div>
                {/* B2B / B2C filter pills */}
                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden bg-white" data-testid="bc-type-filter">
                    {[
                        { k: 'all', label: 'Hepsi' },
                        { k: 'b2b', label: 'B2B' },
                        { k: 'b2c', label: 'B2C' },
                    ].map((opt) => (
                        <button key={opt.k} onClick={() => setFilter(opt.k)}
                            data-testid={`bc-filter-${opt.k}`}
                            className={`h-9 px-3 text-xs font-semibold border-l border-gray-300 first:border-l-0 ${
                                filter === opt.k ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                            }`}>
                            {opt.label}
                        </button>
                    ))}
                </div>
                <select value={sort} onChange={(e) => setSort(e.target.value)}
                    className="h-9 px-2 text-sm border border-gray-300 rounded-md bg-white"
                    data-testid="bc-sort">
                    <option value="last_order">Son sipariş</option>
                    <option value="total_spent">Toplam harcama</option>
                    <option value="orders_count">Sipariş sayısı</option>
                    <option value="name">İsim</option>
                </select>
                <button onClick={load} disabled={loading}
                    className="h-9 px-3 text-sm font-semibold text-white bg-gray-900 hover:bg-black disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    data-testid="bc-refresh">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Yenile
                </button>
                <button onClick={exportXlsx} disabled={exporting || loading || filteredItems.length === 0}
                    className="h-9 px-3 text-sm font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 rounded-md flex items-center gap-1.5"
                    data-testid="bc-export-xlsx"
                    title="Filtrelenmiş listeyi Excel olarak indir">
                    {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                    Excel
                </button>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600 uppercase">
                        <tr>
                            <th className="text-left p-2.5">Müşteri</th>
                            <th className="text-left p-2.5">BTW / VAT</th>
                            <th className="text-left p-2.5">Lokasyon</th>
                            <th className="text-right p-2.5">Sipariş</th>
                            <th className="text-right p-2.5">Ürün</th>
                            <th className="text-right p-2.5">Toplam</th>
                            <th className="text-right p-2.5">Komisyon</th>
                            <th className="text-left p-2.5">Son Sipariş</th>
                            <th className="text-center p-2.5">Tip</th>
                            <th className="text-right p-2.5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredItems.map((c) => (
                            <tr key={c.customer_key} className="border-t border-gray-100 hover:bg-blue-50/30"
                                data-testid={`bc-row-${c.customer_key}`}>
                                <td className="p-2.5">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="font-semibold text-gray-900">{c.display_name}</span>
                                        {c.is_b2b && (
                                            <span className="px-1.5 py-0.5 text-[9px] font-bold bg-indigo-100 text-indigo-800 rounded inline-flex items-center gap-0.5"
                                                data-testid={`bc-b2b-badge-${c.customer_key}`}>
                                                <Briefcase size={9} /> B2B
                                            </span>
                                        )}
                                    </div>
                                    {c.email && (
                                        <div className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5">
                                            <Mail size={10} /> {c.email}
                                        </div>
                                    )}
                                </td>
                                <td className="p-2.5">
                                    {c.vat_number ? (
                                        <div className="font-mono text-[11px] text-gray-900" data-testid={`bc-vat-${c.customer_key}`}>{c.vat_number}</div>
                                    ) : (
                                        <span className="text-gray-300">—</span>
                                    )}
                                    {c.company && (
                                        <div className="text-[10px] text-gray-500 truncate max-w-[160px]" title={c.company}>{c.company}</div>
                                    )}
                                </td>
                                <td className="p-2.5 text-gray-600">
                                    <div className="flex items-center gap-1">
                                        <MapPin size={10} />
                                        {[c.city, c.country].filter(Boolean).join(', ') || '—'}
                                    </div>
                                </td>
                                <td className="p-2.5 text-right">
                                    <div className="font-semibold text-gray-900">{c.orders_count}</div>
                                    <div className="text-[10px] text-gray-500">
                                        {c.open_count > 0 && <span className="text-amber-700">{c.open_count} açık</span>}
                                    </div>
                                </td>
                                <td className="p-2.5 text-right text-gray-700">{c.total_items}</td>
                                <td className="p-2.5 text-right font-semibold text-gray-900">{fmtEur(c.total_spent)}</td>
                                <td className="p-2.5 text-right text-rose-700">{fmtEur(c.total_commission)}</td>
                                <td className="p-2.5 text-gray-600">{c.last_order || '—'}</td>
                                <td className="p-2.5 text-center">
                                    {c.repeat ? (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded">REPEAT</span>
                                    ) : (
                                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-500 rounded">NEW</span>
                                    )}
                                </td>
                                <td className="p-2.5 text-right">
                                    <button onClick={() => setSelected(c)}
                                        className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 ml-auto"
                                        data-testid={`bc-detail-${c.customer_key}`}>
                                        Detay <ExternalLink size={11} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!filteredItems.length && !loading && (
                    <div className="text-center py-8 text-sm text-gray-500">
                        {filter !== 'all'
                            ? `Bu filtrede (${filter.toUpperCase()}) müşteri bulunamadı.`
                            : 'Henüz müşteri yok — Bol siparişlerini sync ettiyseniz tekrar deneyin.'}
                    </div>
                )}
            </div>

            {selected && (
                <CustomerDetailDrawer customerKey={selected.customer_key} preview={selected} onClose={() => setSelected(null)} />
            )}
        </div>
    );
}

function CustomerDetailDrawer({ customerKey, preview, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/api/bol/customers/${encodeURIComponent(customerKey)}`, { headers: hdrs() });
                if (active) setData(r.data);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Detay yüklenemedi');
                onClose();
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [customerKey, onClose]);

    const c = data?.customer;
    const s = data?.stats;

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="bc-drawer">
            <div className="flex-1 bg-black/40" onClick={onClose} />
            <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-start justify-between gap-3 z-10">
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-0.5">Müşteri Detayı</div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <h2 className="text-lg font-bold text-gray-900 truncate">{c?.display_name || preview.display_name}</h2>
                            {(c?.is_b2b || preview.is_b2b) && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-indigo-100 text-indigo-800 rounded inline-flex items-center gap-0.5"
                                    data-testid="bc-drawer-b2b-badge">
                                    <Briefcase size={10} /> B2B
                                </span>
                            )}
                        </div>
                        {(c?.email || preview.email) && (
                            <a href={`mailto:${c?.email || preview.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                                <Mail size={11} /> {c?.email || preview.email}
                            </a>
                        )}
                    </div>
                    <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100"
                        data-testid="bc-drawer-close">
                        <X size={16} />
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-gray-400" size={28} />
                    </div>
                ) : !data ? null : (
                    <div className="p-4 space-y-5">
                        {/* Contact + address */}
                        <Section icon={Building2} title="İletişim & Adres">
                            <KV k="Email" v={c.email} mono />
                            <KV k="Şirket" v={c.company} />
                            <KV k="BTW / VAT" v={c.vat_number} mono />
                            <KV k="İsim" v={`${c.first_name || ''} ${c.last_name || ''}`.trim()} />
                            <KV k="Sokak" v={`${(c.address?.street || '')} ${c.address?.house || ''} ${c.address?.extension || ''}`.trim()} />
                            <KV k="Şehir / Posta" v={[c.address?.city, c.address?.zip].filter(Boolean).join(' / ')} />
                            <KV k="Ülke" v={c.address?.country} />
                        </Section>

                        {/* Stats */}
                        <Section icon={TrendingUp} title="İstatistikler">
                            <div className="grid grid-cols-2 gap-2">
                                <StatBox label="Toplam Sipariş" value={s.orders_count} />
                                <StatBox label="Aktif Sipariş" value={s.active_orders} />
                                <StatBox label="Toplam Ürün" value={s.total_items} />
                                <StatBox label="İptal" value={s.cancelled_orders} />
                                <StatBox label="Toplam Ciro" value={fmtEur(s.total_spent)} highlight />
                                <StatBox label="Komisyon" value={fmtEur(s.total_commission)} negative />
                                <StatBox label="Ort. Sipariş" value={fmtEur(s.avg_order_value)} />
                                <StatBox label="Müşteri Olalı" value={s.first_order || '—'} />
                            </div>
                        </Section>

                        {/* Top products */}
                        {data.top_products?.length > 0 && (
                            <Section icon={Package} title="En Çok Aldığı Ürünler">
                                <div className="space-y-1.5">
                                    {data.top_products.map(p => (
                                        <div key={p.ean} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded text-xs">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-semibold truncate text-gray-900">{p.title || p.ean}</div>
                                                <div className="text-[10px] text-gray-500 font-mono">{p.ean}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-bold text-gray-900">{p.qty} adet</div>
                                                <div className="text-[10px] text-gray-600">{fmtEur(p.revenue)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {/* Orders */}
                        <Section icon={Package} title={`Siparişler (${data.orders.length})`}>
                            <div className="space-y-2">
                                {data.orders.map(o => {
                                    const sb = statusBadge(o.list_status);
                                    return (
                                        <div key={o.order_id} className="border border-gray-200 rounded-lg p-2.5 hover:bg-gray-50">
                                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="font-mono text-[11px] font-bold text-gray-900">#{o.order_number || o.order_id}</span>
                                                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${sb.cls}`}>{sb.txt}</span>
                                                    {o.invoice_requested && (
                                                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-purple-100 text-purple-700">FATURA</span>
                                                    )}
                                                    {o.billit_pushed && (
                                                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-cyan-100 text-cyan-700">BILLIT</span>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-gray-500 shrink-0">{o.order_date}</div>
                                            </div>
                                            <div className="text-[11px] text-gray-600 space-y-0.5">
                                                {(o.lines || []).map((ln, i) => (
                                                    <div key={i} className="flex justify-between gap-2">
                                                        <span className="truncate">{ln.quantity}× {ln.title || ln.ean}</span>
                                                        <span className="font-mono text-gray-800 shrink-0">{fmtEur(ln.line_total_incl_vat)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-gray-100">
                                                <div className="text-[10px] text-gray-500">
                                                    {o.tracking?.transporter_code && (
                                                        <span className="flex items-center gap-1">
                                                            <Truck size={10} /> {o.tracking.transporter_code}
                                                            {o.tracking?.track_and_trace && ` · ${o.tracking.track_and_trace.slice(0, 14)}`}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs font-bold text-gray-900">{fmtEur(o.total_incl_vat)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Section>
                    </div>
                )}
            </div>
        </div>
    );
}

function Card({ label, value, icon: Icon, color }) {
    const colorMap = {
        blue: 'bg-blue-50 text-blue-700', emerald: 'bg-emerald-50 text-emerald-700',
        amber: 'bg-amber-50 text-amber-700', purple: 'bg-purple-50 text-purple-700',
        indigo: 'bg-indigo-50 text-indigo-700',
    };
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${colorMap[color]}`}><Icon size={14} /></div>
            </div>
            <div className="text-xl font-black text-gray-900">{value}</div>
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className="text-gray-600" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700">{title}</h3>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 bg-white">
                {children}
            </div>
        </div>
    );
}

function KV({ k, v, mono }) {
    if (!v) return null;
    return (
        <div className="flex justify-between gap-2 py-1 text-xs border-b border-gray-50 last:border-0">
            <span className="text-gray-500 shrink-0">{k}</span>
            <span className={`text-gray-900 font-semibold text-right ${mono ? 'font-mono' : ''}`}>{v}</span>
        </div>
    );
}

function StatBox({ label, value, highlight, negative }) {
    return (
        <div className={`rounded p-2 ${highlight ? 'bg-emerald-50' : negative ? 'bg-rose-50' : 'bg-gray-50'}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`text-sm font-bold ${highlight ? 'text-emerald-700' : negative ? 'text-rose-700' : 'text-gray-900'}`}>{value}</div>
        </div>
    );
}
